import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../config";
import { recordVaultActivity } from "../secure/VaultPicker";

import {
  ArrowLeft,
  Check,
  Send,
  FileText,
  X,
  Pencil,
  Users,
  ShieldCheck,
  Lock,
  UserCheck,
  Clock,
  ClipboardList,
  ExternalLink,
} from "lucide-react";

const STEPS = ["Upload Document", "Add Recipients", "Review & Send"];
const DEFAULT_MESSAGE = "Please review and sign the attached document.";
const REMINDER_LABELS = { none: "No reminders", 1: "Every day", 3: "Every 3 days", 7: "Every week" };
const PREVIEWABLE = ["pdf", "png", "jpg", "jpeg", "txt"];
const AVATAR_COLORS = ["bg-indigo-500", "bg-purple-500", "bg-emerald-500", "bg-amber-500", "bg-sky-500", "bg-rose-500"];

const toDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parseLocalDate = (str) => {
  const [y, m, d] = String(str).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const formatDeadline = (str) =>
  parseLocalDate(str).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const initials = (name, email) => {
  const parts = (name || email || "?").trim().split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[1][0] : "")).toUpperCase();
};

const inputCls =
  "w-full border border-gray-200 rounded-lg px-3 text-sm text-gray-800 bg-white outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-3 ${className}`}>{children}</div>
);

const CardTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-2.5">
      <Icon size={17} className="text-indigo-600" />
      <h3 className="text-sm font-semibold text-gray-900">{children}</h3>
    </div>
    {right}
  </div>
);

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3 py-1.5 border-t border-gray-100 first:border-t-0 first:pt-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 pt-0.5">{label}</p>
    <div className="text-sm text-gray-800 break-words min-w-0">{children}</div>
  </div>
);

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-center justify-between gap-4 py-1">
    <div className="flex items-center gap-2.5 text-xs text-gray-500">
      <Icon size={14} className="text-gray-400" />
      {label}
    </div>
    <div className="text-xs font-medium text-gray-800 text-right">{children}</div>
  </div>
);

const RolePill = ({ role }) => (
  <span
    className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
      role === "Approver" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700"
    }`}
  >
    {role}
  </span>
);

/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */

const pluralS = (n) => (n === 1 ? "" : "s");

const pagesLabel = (pages) => (pages ? ` • ${pages} page${pluralS(pages)}` : "");

const defaultDeadline = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toDateInput(d);
};

// What the request will actually use (Edit Details changes these)
const initialFields = (uploadedFile, initialMessage, meta) => ({
  title: meta.title || (uploadedFile?.name || "").replace(/\.[^.]+$/, ""),
  message: initialMessage || meta.message || DEFAULT_MESSAGE,
  deadline: meta.deadline || defaultDeadline(),
});

// Each recipient carries their own authentication method (chosen on the previous page)
const verificationFor = (recipients, fallback) => {
  const methods = [...new Set((recipients || []).map((r) => r.auth).filter(Boolean))];
  return methods.join(", ") || fallback || "";
};

const validateEdit = ({ title, message, deadline }) => {
  if (!title.trim()) return "Please enter a request title.";
  if (!message.trim()) return "Please enter a message.";
  if (!deadline || deadline < toDateInput(new Date())) return "The deadline can't be in the past.";
  return "";
};

const sendProblem = (user, uploadedFile, recipients) => {
  if (!user.email) return "Your session has expired. Please log in again.";
  if (!uploadedFile?.file) return "The document is missing. Please go back and upload it again.";
  if (!recipients?.length) return "Please add at least one recipient.";
  return "";
};

const buildRequestForm = ({ user, uploadedFile, recipients, signingOrder, title, message, deadline, verificationMethod }) => {
  const formData = new FormData();
  formData.append("sender_email", user.email);
  formData.append("sender_name", user.name || "");
  formData.append(
    "recipients",
    // order of this array IS the signing order
    JSON.stringify(recipients.map(({ id, name, email, role }) => ({ id, name, email, role })))
  );
  formData.append("subject", title || `Signature Request - ${uploadedFile.name}`);
  formData.append("message", message);
  formData.append("file", uploadedFile.file);
  formData.append("signing_order", String(!!signingOrder));
  formData.append("verification_method", verificationMethod);
  formData.append("expires_at", deadline);
  return formData;
};

/* ------------------------------------------------------------------ */
/* Hook: sending the request                                           */
/* ------------------------------------------------------------------ */

function useSendRequest({ navigate, uploadedFile, recipients, signingOrder, fields, verificationMethod }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (sending) return;
    setError("");

    const user = JSON.parse(localStorage.getItem("user")) || {};
    const problem = sendProblem(user, uploadedFile, recipients);
    if (problem) return setError(problem);

    setSending(true);
    try {
      const formData = buildRequestForm({ user, uploadedFile, recipients, signingOrder, verificationMethod, ...fields });
      const response = await fetch(`${API_URL}/api/workflow/requests`, { method: "POST", body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Request failed");

      // File came from Secure Vault: record that the request was actually sent
      if (uploadedFile?.vaultFileId) {
        await recordVaultActivity(
          uploadedFile.vaultFileId,
          "REQUEST_SENT",
          `Request ${data.request_id || ""} sent to ${recipients.length} recipient${pluralS(recipients.length)}`
        );
      }

      navigate("/request-success", {
        state: {
          uploadedFile,
          recipients,
          savedExpiry: parseLocalDate(fields.deadline),
          signingOrder,
          requestId: data.request_id,
        },
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not send the request.");
    } finally {
      setSending(false);
    }
  };

  return { sending, error, send };
}

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

const Stepper = ({ currentStep }) => (
  <div className="px-6 py-1">
    <div className="flex items-start">
      {STEPS.map((step, index) => {
        const reached = index <= currentStep;
        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== STEPS.length - 1 && (
              <div
                className={`absolute top-4 h-[2px] ${reached ? "bg-indigo-500" : "bg-gray-200"}`}
                style={{ left: "calc(50% + 22px)", width: "calc(100% - 44px)" }}
              />
            )}
            <div
              className={`z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                reached ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              {index < currentStep ? <Check size={14} /> : index + 1}
            </div>
            <p className={`mt-1.5 text-xs font-medium text-center ${index === currentStep ? "text-indigo-600" : "text-gray-500"}`}>
              {step}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const DocumentPreview = ({ uploadedFile }) => {
  const ext = extOf(uploadedFile?.name);
  const canPreview = !!uploadedFile?.url && PREVIEWABLE.includes(ext);
  const noPreviewText = uploadedFile ? "Preview isn't available for this file type." : "Go back and upload a document.";
  return (
    <Card className="flex flex-col">
      <CardTitle
        icon={FileText}
        right={
          uploadedFile?.url && (
            <a
              href={uploadedFile.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <ExternalLink size={13} /> Open in new tab
            </a>
          )
        }
      >
        Document Preview
      </CardTitle>

      <div className="relative rounded-lg bg-gray-100 border border-gray-200 overflow-hidden flex-1 min-h-[420px]">
        {canPreview ? (
          <iframe src={uploadedFile.url} title="Document Preview" className="absolute inset-0 w-full h-full border-none" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
            <div className={`w-10 h-12 rounded-md flex items-center justify-center text-[11px] font-bold text-white ${ext === "pdf" ? "bg-red-500" : "bg-indigo-500"}`}>
              {(ext || "FILE").toUpperCase().slice(0, 4)}
            </div>
            <p className="text-sm font-medium text-gray-700">{uploadedFile?.name || "No document attached"}</p>
            <p className="text-xs text-gray-400">{noPreviewText}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

const SummaryCard = ({ uploadedFile, fields, signingOrder, meta, onEdit }) => {
  const orderText = signingOrder
    ? "One after another, in the order listed below"
    : "All at once, everyone is notified immediately";
  return (
    <Card>
      <CardTitle
        icon={ClipboardList}
        right={
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1.5 h-7 px-3 border border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-xs font-medium transition"
          >
            <Pencil size={12} /> Edit Details
          </button>
        }
      >
        Request Summary
      </CardTitle>

      <Row label="Request Title">{fields.title || "—"}</Row>
      <Row label="Document">
        <span className="font-medium">{uploadedFile?.name || "—"}</span>
        <span className="block text-xs text-gray-500">
          {uploadedFile?.size || ""}
          {pagesLabel(uploadedFile?.pages)}
        </span>
      </Row>
      <Row label="Message">
        <span className="text-gray-600 leading-relaxed">{fields.message}</span>
      </Row>
      <Row label="Expires On">{formatDeadline(fields.deadline)}</Row>
      <Row label="Reminders">{REMINDER_LABELS[meta.reminder] || "—"}</Row>
      <Row label="Signing Order">{orderText}</Row>
      {meta.requireAll !== undefined && (
        <Row label="Completion">{meta.requireAll ? "All recipients must complete" : "Not required for all"}</Row>
      )}
    </Card>
  );
};

const RecipientItem = ({ r, index, signingOrder }) => (
  <div className="flex items-center justify-between gap-2">
    <div className="flex items-center gap-2.5 min-w-0">
      <div
        className={`w-7 h-7 rounded-full text-white text-[11px] font-semibold flex items-center justify-center shrink-0 ${
          AVATAR_COLORS[index % AVATAR_COLORS.length]
        }`}
      >
        {initials(r.name, r.email)}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-800 truncate">
          {signingOrder && <span className="text-gray-400 mr-1">{index + 1}.</span>}
          {r.name || r.email}
        </p>
        <p className="text-[11px] text-gray-500 truncate">
          {r.email}
          {r.auth ? ` · ${r.auth}` : ""}
        </p>
      </div>
    </div>
    <RolePill role={r.role} />
  </div>
);

const RecipientsCard = ({ recipients, signingOrder }) => (
  <Card>
    <CardTitle icon={Users}>
      Recipients <span className="text-gray-500 font-medium">({recipients?.length || 0})</span>
    </CardTitle>
    {recipients?.length ? (
      <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
        {recipients.map((r, i) => (
          <RecipientItem key={r.id ?? r.email} r={r} index={i} signingOrder={signingOrder} />
        ))}
      </div>
    ) : (
      <p className="text-xs text-gray-400">No recipients added.</p>
    )}
  </Card>
);

const SecurityCard = ({ verificationMethod }) => (
  <Card>
    <CardTitle icon={ShieldCheck}>Security &amp; Trust</CardTitle>
    <div className="bg-gray-50 rounded-lg px-3 py-1 divide-y divide-gray-100">
      <InfoRow icon={Lock} label="Signing Method">
        Hybrid: Post-Quantum + Classical
      </InfoRow>
      <InfoRow icon={UserCheck} label="Verification">
        {verificationMethod || "—"}
      </InfoRow>
      <InfoRow icon={Clock} label="Timestamp">
        Enabled
      </InfoRow>
      <InfoRow icon={ClipboardList} label="Audit Trail">
        Enabled
      </InfoRow>
    </div>
  </Card>
);

const FooterBar = ({ error, sending, onBack, onSend }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
    <button
      type="button"
      onClick={onBack}
      className="flex items-center gap-2 h-9 px-6 border border-indigo-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition"
    >
      <ArrowLeft size={14} /> Back
    </button>

    <div className="flex-1 min-w-[160px] text-center text-xs">{error && <span className="text-red-500">{error}</span>}</div>

    <button
      type="button"
      onClick={onSend}
      disabled={sending}
      className="flex items-center gap-2 h-9 px-5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg text-xs font-medium transition"
    >
      <Send size={14} /> {sending ? "Sending..." : "Send Request"}
    </button>
  </div>
);

const EditModal = ({ initial, onSave, onClose }) => {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState("");
  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const save = () => {
    const problem = validateEdit(values);
    if (problem) return setError(problem);
    onSave({ title: values.title.trim(), message: values.message.trim(), deadline: values.deadline });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl w-full max-w-md p-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Edit Details</h3>
          <button type="button" onClick={onClose}>
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Request Title</label>
            <input type="text" value={values.title} maxLength={100} onChange={setField("title")} className={`${inputCls} h-9`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Message to Recipients</label>
            <textarea rows={3} value={values.message} maxLength={500} onChange={setField("message")} className={`${inputCls} py-2 resize-none`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Expires On</label>
            <input
              type="date"
              value={values.deadline}
              min={toDateInput(new Date())}
              onChange={setField("deadline")}
              className={`${inputCls} h-9`}
            />
          </div>
        </div>

        {error && <p className="text-xs text-red-500 mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button type="button" onClick={save} className="h-9 px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function ReviewAndSend() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { uploadedFile, recipients, message: initialMessage, signingOrder, requestMeta } = state || {};
  const currentStep = 2;
  const meta = requestMeta || {};

  const [fields, setFields] = useState(() => initialFields(uploadedFile, initialMessage, meta));
  const [showEdit, setShowEdit] = useState(false);

  const verificationMethod = verificationFor(recipients, state?.verificationMethod);
  const { sending, error, send } = useSendRequest({
    navigate, uploadedFile, recipients, signingOrder, fields, verificationMethod,
  });

  const saveEdit = (values) => {
    setFields(values);
    setShowEdit(false);
  };

  return (
    <div className="w-full space-y-3" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Stepper currentStep={currentStep} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_350px] gap-3 items-stretch">
        {/* ========================= LEFT — preview ========================= */}
        <DocumentPreview uploadedFile={uploadedFile} />

        {/* ========================= RIGHT — summary ========================= */}
        <div className="space-y-3">
          <SummaryCard
            uploadedFile={uploadedFile}
            fields={fields}
            signingOrder={signingOrder}
            meta={meta}
            onEdit={() => setShowEdit(true)}
          />
          <RecipientsCard recipients={recipients} signingOrder={signingOrder} />
          <SecurityCard verificationMethod={verificationMethod} />
        </div>
      </div>

      <FooterBar error={error} sending={sending} onBack={() => navigate(-1)} onSend={send} />

      {showEdit && <EditModal initial={fields} onSave={saveEdit} onClose={() => setShowEdit(false)} />}
    </div>
  );
}